// The leaderboard: pubs in the current map view ranked by score, "right now"
// or by their best window today/tonight. Recomputed when the map settles,
// through the same cell cache and session cap as the score pins.

import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import type { AreaScore } from '../data/live';
import { WEATHER_CELL_DEG } from '../data/liveConfig';
import { hasOutdoorSpace, summary, type PubStore, type PubSummary } from '../data/pubs';
import type { BestWindow } from '../score/bestWindow';
import type { HourScore } from '../score/types';
import { ukParts } from '../lib/time';
import type { MapViewHandle } from './MapView';

export type LeaderboardMode = 'now' | 'best';

/** "Best tonight" after 5pm, otherwise "Best today". */
export function bestLabel(now = Date.now()): string {
  return ukParts(now).hour >= 17 ? 'Best tonight' : 'Best today';
}

/** Rank this many pubs nearest the middle of the view. */
export const LEADERBOARD_PUBS = 60;
/** ...as long as they fit in this many weather cells (otherwise: zoom in). */
export const LEADERBOARD_MAX_CELLS = 12;
const MOVE_DEBOUNCE_MS = 450;

export interface LeaderboardRow {
  pub: PubSummary;
  /** The hour the rank is based on: now, or the best hour. */
  hour: HourScore;
  best: BestWindow | null;
  /** The number it's ranked by. */
  score: number;
}

export type LeaderboardState =
  | { status: 'idle' }
  | { status: 'zoom' }
  | { status: 'empty' }
  | { status: 'loading'; rows: LeaderboardRow[] }
  | { status: 'ready'; rows: LeaderboardRow[]; problem: 'capped' | 'failed' | null };

/** Rank scored pubs: highest first; ties go to confirmed outside space, then the name. */
export function rankRows(
  scores: readonly AreaScore[],
  store: PubStore,
  mode: LeaderboardMode,
): LeaderboardRow[] {
  const rows: LeaderboardRow[] = [];
  for (const s of scores) {
    const pub = summary(store, s.index);
    if (mode === 'now') {
      rows.push({ pub, hour: s.now, best: null, score: s.now.total });
      continue;
    }
    const f = s.forecast;
    if (!f) continue;
    const bestHour = f.bestIndex !== null ? f.hours[f.bestIndex] : undefined;
    const hour = bestHour ?? s.now;
    rows.push({ pub, hour, best: f.best, score: f.best ? f.best.peak : hour.total });
  }
  const confirmed = (r: LeaderboardRow) => hasOutdoorSpace(r.pub.flags);
  return rows.sort(
    (a, b) =>
      b.score - a.score ||
      Number(confirmed(b)) - Number(confirmed(a)) ||
      a.pub.name.localeCompare(b.pub.name),
  );
}

export function useLeaderboard(
  mapRef: RefObject<MapViewHandle | null>,
  store: PubStore | null,
  open: boolean,
  mode: LeaderboardMode,
) {
  const [state, setState] = useState<LeaderboardState>({ status: 'idle' });
  const timer = useRef<number | undefined>(undefined);
  const generation = useRef(0);

  const update = useCallback(() => {
    const run = ++generation.current;
    if (!open || !store) {
      setState({ status: 'idle' });
      return;
    }
    const { indices } = mapRef.current?.pubsInView(LEADERBOARD_PUBS) ?? { indices: [] };
    if (indices.length === 0) {
      setState({ status: 'empty' });
      return;
    }
    const pubs = indices.map((index) => summary(store, index));
    const cells = new Set(
      pubs.map(
        (p) => `${Math.round(p.lat / WEATHER_CELL_DEG)}:${Math.round(p.lon / WEATHER_CELL_DEG)}`,
      ),
    );
    if (cells.size > LEADERBOARD_MAX_CELLS) {
      setState({ status: 'zoom' });
      return;
    }
    setState((current) => ({
      status: 'loading',
      rows: current.status === 'ready' || current.status === 'loading' ? current.rows : [],
    }));
    import('../data/live')
      .then(({ loadAreaScores }) => loadAreaScores(pubs, { full: mode === 'best' }))
      .then(({ scores, problem }) => {
        if (run !== generation.current) return;
        setState({ status: 'ready', rows: rankRows(scores, store, mode), problem });
      })
      .catch(() => {
        if (run === generation.current) setState({ status: 'ready', rows: [], problem: 'failed' });
      });
  }, [mapRef, store, open, mode]);

  /** Call on map move end: waits for the map to settle. */
  const onMoveEnd = useCallback(() => {
    if (!open) return;
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(update, MOVE_DEBOUNCE_MS);
  }, [update, open]);

  // Opening, switching mode or new filters apply straight away.
  useEffect(() => {
    timer.current = window.setTimeout(update, 0);
    return () => window.clearTimeout(timer.current);
  }, [update]);

  return { state: open ? state : ({ status: 'idle' } as const), onMoveEnd, refresh: update };
}
